import { AddOutlined, Refresh } from '@mui/icons-material';
import { Box, Button, IconButton } from '@mui/material';
import { camelCase } from 'lodash';
// or write a tiny util
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import VNCheckBox from '@/components/VNCheckBox/VNCHeckBox';
import VNDictionaryField from '@/components/VNDictionaryField/VNDictionaryField';
import VNSelect from '@/components/VNSelect/VNSelect';
import VNTextField from '@/components/VNTextField/VNTextField';
import { useProgress } from '@/contexts/ProgressProvider';
import VnModalBody from '@/modals/components/VnModalBody';
import VnModalFooter from '@/modals/components/VnModalFooter';
import { useModalContext } from '@/modals/VNModalProvider';
import { ChannelConfigType, type NotificationHandler } from '@/models/ExternalNotification';
import { ExternalNotificationService } from '@/services/external-notification-service';
import { applicationDataSelector } from '@/store/slices/application-data';
import { fuelUserAccessSliceSelector } from '@/store/slices/fuelUserAccess';
import useAppUtils from '@/utils/appUtils';

import Recipents from './Recipients';
import { validateChannelHandlerForm } from './validateWorkflow';

// ----------------- Main Form -----------------
const NotificationHanlderEditor = ({ id = 'new', addToList, workflow }) => {
  const [initialized, setInitialized] = useState(false);
  const { loading, setProgress } = useProgress();
  const { alert } = useAppUtils();
  const { onClose } = useModalContext();
  const { permissions, fuelUserAccessData } = useSelector(fuelUserAccessSliceSelector);
  const { ouData, switchOuData } = useSelector(applicationDataSelector);
  const { site, eventTypes, handlerChannelTypes, recipientTypes, placeholders, notificationConfig } = workflow;
  const navigate = useNavigate();
  // console.log(' { eventTypes, handlerChannelTypes, recipientTypes, placeholders, notificationConfig } ', {
  //   eventTypes,
  //   handlerChannelTypes,
  //   recipientTypes,
  //   placeholders,
  //   notificationConfig,
  //   site,
  // });

  const methods = useForm<NotificationHandler>({
    defaultValues: {
      id: crypto.randomUUID(),
      groupId: ouData?.ouId,
      eventType: '',
      enabled: false,
      name: '',
      channelConfig: {
        channelType: ChannelConfigType.Unknown,
      } as any,
    },
  });

  const { getValues, handleSubmit, watch, setValue, reset, control } = methods;
  const eventTypeWatchValue = useWatch({ control, name: 'eventType' });

  const channelType = watch('channelConfig.channelType');
  const templateList = watch('tmp.templateList');

  useEffect(() => {
    if (eventTypeWatchValue && workflow?.placeholders) {
      const list = placeholders.filter((p) => p.name === eventTypeWatchValue);
      setValue('tmp.placeholders', list);
    }
  }, [eventTypeWatchValue]);
  const getTemplateList = async () => {
    try {
      const templates = await ExternalNotificationService.getTemplateList(ouData?.ouId);
      setValue('tmp.templateList', templates);
    } catch (ex) {
      console.error('Unable to get templates', { ex });
    }
  };

  const init = async () => {
    try {
      setProgress(true);

      if (id !== 'new') {
        const channelHandler = await ExternalNotificationService.getHandler(id);
        reset(channelHandler);
      }
      const recipientValues = {
        UserId: fuelUserAccessData.userId,
        OUId: ouData.ouId,
        Email: fuelUserAccessData.email,
      };
      await getTemplateList();
      setValue('tmp.recipientValues', recipientValues);
      setTimeout(() => {
        setInitialized(true);
      }, 0);
    } catch (ex) {
      console.log('Unable to load notification config and details');
    } finally {
      setProgress(false);
    }
  };
  const validateForm = (data: NotificationHandler) => {
    const manualErrors = validateChannelHandlerForm(data, notificationConfig);
    if (manualErrors.errors.length > 0) {
      manualErrors.errors.forEach((error) => {
        console.error(`${error.fieldId}: ${error.message}`);
      });
      const message = `Please fix validation errors before saving: ${manualErrors.errors[0].message}`;
      alert(message, 'error');
      return false;
    }
    return true;
  };

  const onSubmit = async (data: NotificationHandler) => {
    // console.log('Final DTO:', data);
    const isValid = validateForm(data);
    if (!isValid) {
      return;
    }
    try {
      setProgress(true, 'Saving...');
      delete data.tmp;
      const response = await ExternalNotificationService.upsertHandler(data);
      if (response) {
        alert('Notification handler updated');
        addToList();
      }
    } catch (ex) {
      console.log('Unable to save notification handler', { ex });
    } finally {
      setProgress(false);
    }
  };

  const openNewTemplateDialog = () => {
    window.open('/event-workflows/templates', '_blank');
  };

  useEffect(() => {
    init();
  }, [id, workflow]);

  return (
    <>
      <VnModalBody>
        <div className="notification-handler-editor">
          {initialized && (
            <FormProvider {...methods}>
              <form onSubmit={handleSubmit(onSubmit)} className="form">
                <Box
                  // p={2}
                  sx={{
                    '& > *': {
                      mb: 2,
                    },
                    p: 1,
                  }}
                >
                  {/* Group Id */}
                  <VNTextField name="name" label="Name" required />
                  <VNSelect name="eventType" options={eventTypes} label="Event Type" required />
                  <VNSelect
                    name="channelConfig.channelType"
                    options={handlerChannelTypes}
                    label="Notification Provider"
                    required
                  />
                  <VNCheckBox name="enabled" label="Enabled" />
                  {/* Conditional Channel Config */}
                  {channelType && (
                    <Box
                      sx={{
                        '& > *': {
                          mb: 2,
                        },
                      }}
                    >
                      {notificationConfig?.handlerChannelTypes[channelType]?.attributes?.map((field) => {
                        const fieldName = `channelConfig.${camelCase(field.name)}`;
                        const rules = field.isRequired ? { required: `${field.description} is required` } : {};
                        const allowEditRecipientConfig = eventTypeWatchValue
                          ? notificationConfig?.eventTypes[eventTypeWatchValue]?.allowEditRecipientConfig
                          : false;
                        const predefinedRecipientConfig = eventTypeWatchValue
                          ? notificationConfig?.eventTypes[eventTypeWatchValue]?.predefinedRecipientConfig
                          : null;
                        const isArray = field.type.endsWith('[]');

                        const isDictionary = field.type.toLowerCase().includes('dictionary');

                        if (field.isTemplateId) {
                          const newTemplateList = templateList.filter((t) => field.templateTypes.includes(t.templateType));
                          return (
                            <Box sx={{ display: 'flex', gap: 1 }}>
                              <VNSelect
                                key={field.name}
                                name={fieldName}
                                label={field.description}
                                options={newTemplateList.map((v) => ({
                                  value: v.id,
                                  label: v.name,
                                }))}
                                required={field.isRequired}
                                defaultValue={field.defaultValue ?? ''}
                                clearOption
                              />
                              <IconButton size="small" color="success" onClick={openNewTemplateDialog}>
                                {' '}
                                <AddOutlined titleAccess="Add New Template" />
                              </IconButton>
                              <IconButton size="small" color="success" onClick={getTemplateList}>
                                {' '}
                                <Refresh titleAccess="Refresh Templates" />
                              </IconButton>
                            </Box>
                          );
                        }
                        if (field.type.startsWith('RecipientConfig')) {
                          const allowedList = Array.isArray(field.allowedValues) ? field.allowedValues : [];
                          const allowedSet = new Set(allowedList.map((v) => String(v).toLowerCase()));
                          const recipientOptions =
                            allowedList.length > 0
                              ? recipientTypes.filter(
                                  (opt) =>
                                    allowedSet.has(String(opt.value).toLowerCase()) ||
                                    allowedSet.has(String(opt.label).toLowerCase())
                                )
                              : recipientTypes;
                          // const recipientValues = getValues('tmp.recipientValues');
                          // const recipients = Object.keys(recipientValues).map((key) => ({
                          //   recipientType: RecipientType[key as keyof typeof RecipientType],
                          //   value: recipientValues[key as keyof typeof recipientValues],
                          // }));
                          // setValue(fieldName, recipients);
                          return (
                            <Recipents
                              key={field.name}
                              name={fieldName}
                              label={field.description}
                              type={field.type}
                              required={field.isRequired}
                              options={recipientOptions}
                              allowEditRecipientConfig={allowEditRecipientConfig}
                              predefinedRecipientConfig={predefinedRecipientConfig}
                              supportsTemplateExpressions={field.supportsTemplateExpressions}
                              placeholders={watch('tmp.placeholders')}
                            />
                          );
                        }
                        if (field.allowedValues && field.allowedValues.length > 0) {
                          // Dropdown for allowed values
                          return (
                            <VNSelect
                              key={field.name}
                              name={fieldName}
                              label={field.description}
                              options={field.allowedValues.map((v) => ({
                                value: v,
                                label: v,
                              }))}
                              required={field.isRequired}
                              defaultValue={field.defaultValue ?? ''}
                            />
                          );
                        }
                        if (isDictionary) {
                          return (
                            <VNDictionaryField
                              key={field.name}
                              name={fieldName}
                              label={field.description}
                              required={field.isRequired}
                            />
                          );
                        }

                        // Default: text/password field
                        return (
                          <VNTextField
                            key={field.name}
                            name={fieldName}
                            type={field.isSecret ? 'password' : 'text'}
                            label={field.description}
                            required={field.isRequired}
                            defaultValue={field.defaultValue ?? ''}
                            placeholders={field.supportsTemplateExpressions ? watch('tmp.placeholders') : []}
                          />
                        );
                      })}
                    </Box>
                  )}
                </Box>
              </form>
            </FormProvider>
          )}
        </div>
      </VnModalBody>
      <VnModalFooter
        right={
          <>
            {' '}
            <Button variant="contained" sx={{ mr: 1 }} color="success" onClick={handleSubmit(onSubmit)} disabled={loading}>
              Save
            </Button>
            <Button variant="contained" sx={{ mr: 1 }} onClick={() => onClose()}>
              Cancel
            </Button>
          </>
        }
      />
    </>
  );
};

export default NotificationHanlderEditor;
